import { zodResolver } from "@hookform/resolvers/zod"
import { useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { Navigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../../components/DiMaDetailsContent"
import { useApplicationKeyValuePairTextFieldRenderers } from "../../../../../components/Helpers"
import {
  ApplicationKeyValuePairGet,
  ApplicationKeyValuePairPut,
} from "../../../../../models/ApplicationKeyValuePair"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../../models/Authorization"
import { UserInfo } from "../../../../../models/User"

const validationSchema = z.object({
  key: z.string().min(1, "Key is required"),
  value: z.string().min(1, "Value is required"),
})

export function ApplicationKeyValuePairDetails(props: {
  readonly applicationKeyValuePair: ApplicationKeyValuePairGet
  readonly applicationId: string
  readonly user: UserInfo
  readonly setApplicationKeyValuePair: (
    updatedApplicationKeyValuePair: ApplicationKeyValuePairGet
  ) => void
}) {
  const { applicationKeyValuePair, applicationId, user, setApplicationKeyValuePair } =
    props

  const returnURL = `/applications/${applicationId}#key-value-pairs`

  const formMethods = useForm<ApplicationKeyValuePairPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      key: applicationKeyValuePair.key,
      value: applicationKeyValuePair.value,
    })
  }, [applicationKeyValuePair.key, applicationKeyValuePair.value, reset])

  const onSubmit = useCallback(
    (values: ApplicationKeyValuePairPut) =>
      digitalManagerApi
        .put<ApplicationKeyValuePairGet>(
          `/applications/${applicationId}/keyValuePairs/${applicationKeyValuePair.id}`,
          {
            key: values.key,
            value: values.value,
          }
        )
        .then((res) => {
          toast.success("Application key-kalue pair updated")
          setApplicationKeyValuePair(res.data)
        })
        .catch(() => {
          toast.error("Cannot update application key-value pair")
          throw new Error("Cannot update application key-value pair")
        }),
    [applicationId, applicationKeyValuePair.id, setApplicationKeyValuePair]
  )

  const handleKeyChange = useCallback(
    (field: ControllerRenderProps<ApplicationKeyValuePairPut, "key">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const handleValueChange = useCallback(
    (field: ControllerRenderProps<ApplicationKeyValuePairPut, "value">) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const { renderTextFieldKey, renderTextFieldValue } =
    useApplicationKeyValuePairTextFieldRenderers<ApplicationKeyValuePairPut>({
      errors,
      handleKeyChange,
      handleValueChange,
    })

  if (!applicationKeyValuePair) {
    return <Navigate to={returnURL} />
  }

  const detailsContentProps: DiMaDetailsContentProps<ApplicationKeyValuePairPut> = {
    formMethods,
    canWrite: hasUserAccessToApplicationFeature(
      user.authorizations.applications,
      applicationId,
      DimaFeatureShortNameEnum.AM,
      AccessLevel.Write
    ),
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Key",
        displayItem: <div className="title">{applicationKeyValuePair.key}</div>,
        editItem: <Controller name="key" control={control} render={renderTextFieldKey} />,
      },
      {
        id: 1,
        label: "Value",
        displayItem: <div className="title">{applicationKeyValuePair.value}</div>,
        editItem: (
          <Controller name="value" control={control} render={renderTextFieldValue} />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
